/**
 * SingleToggleChipPopover
 * Reusable single-select filter popover with toggle chips + APPLY/Reset.
 * Like ToggleChipPopover but only one value can be selected at a time.
 * Used for: Price, Format, Duration on pages with single-select filters.
 */

import { useCallback, useState } from "react"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { FilterChipButton } from "./FilterChipButton"
import { PopoverFooter } from "./PopoverFooter"
import { ToggleChip } from "./ToggleChip"

interface SingleToggleChipOption {
  value: string
  label: string
}

interface SingleToggleChipPopoverProps {
  label: string
  options: SingleToggleChipOption[]
  /** Currently committed value (undefined = none selected) */
  selected: string | undefined
  /** Called when user clicks APPLY */
  onApply: (value: string | undefined) => void
  /** Value that means "all" / no filter (e.g. "all") -- treated as unselected for display */
  allValue?: string
  /** Optional: render extra content below the chips (e.g. price slider) */
  extraContent?: (
    local: string | undefined,
    setLocal: React.Dispatch<React.SetStateAction<string | undefined>>,
  ) => React.ReactNode
}

export function SingleToggleChipPopover({
  label,
  options,
  selected,
  onApply,
  allValue,
  extraContent,
}: SingleToggleChipPopoverProps) {
  const [open, setOpen] = useState(false)
  const [local, setLocal] = useState<string | undefined>(selected)

  const handleOpen = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) setLocal(selected)
      setOpen(nextOpen)
    },
    [selected],
  )

  // For display: if selected equals allValue, show nothing
  const isActive = selected !== undefined && selected !== allValue
  const displayLabel = isActive
    ? (options.find(o => o.value === selected)?.label ?? selected)
    : undefined

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <FilterChipButton
          label={`${label}${displayLabel ? ":" : ""}`}
          value={displayLabel}
          isActive={open || isActive}
        />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={10}
        className="w-auto bg-popover border border-border rounded-[20px] p-[20px]"
      >
        <div className="flex flex-col gap-[20px]">
          <div className="flex flex-wrap items-center gap-[10px]">
            {options.map(option => (
              <ToggleChip
                key={option.value}
                label={option.label}
                selected={local === option.value}
                onToggle={() => setLocal(local === option.value ? allValue : option.value)}
              />
            ))}
          </div>
          {extraContent?.(local, setLocal)}
          <PopoverFooter
            onApply={() => {
              onApply(local)
              setOpen(false)
            }}
            onReset={() => setLocal(allValue)}
          />
        </div>
      </PopoverContent>
    </Popover>
  )
}
